import React, { useState, useEffect, useMemo } from 'react';
import { getErrorMessage } from '../utils/api';
import { buildApiUrl } from '../config/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
} from 'recharts';
import logo from '../assets/futeval-logo.png';
import './Dashboard.css';

interface PlayerDashboardProps {
  onBackToLanding?: () => void;
  onExit?: () => void; // New prop for exiting to login
  onBackToCoachDashboard?: () => void; // New prop for returning to coach dashboard
  playerId?: number | null;
  evaluationData?: any; // Optional pre-loaded evaluation data
  viewedByCoach?: boolean; // Flag to indicate if viewed by a coach
}

interface Evaluation {
  id: number;
  player: {
    first_name: string;
    last_name: string;
    age: number;
    position: string;
    gender?: string;
    club?: string;
    team?: string;
    group?: string;
  };
  scores: Record<string, number>;
  category_scores: Record<string, number>;
  weighted_index: number;
  ai_feedback: string | null;
  created_at: string;
  updated_at?: string | null;
}

interface AgeGroupAverages {
  success: boolean;
  age: number;
  count: number;
  use_benchmark: boolean;
  averages: Record<string, number>;
}

// Using buildApiUrl helper instead of API_BASE to ensure correct /api prefix

const INDICATOR_GROUPS = {
  Technical: ['first_touch', 'passing_accuracy', 'ball_control', 'use_of_both_feet', 'shooting'],
  Tactical: ['field_vision', 'decision_making', 'positioning', 'off_the_ball_movement', 'tackling'],
  Physical: ['speed', 'strength', 'endurance', 'height', 'weight', 'change_of_pace'],
  'Psycho-Social': ['resilience', 'communication', 'leadership', 'concentration', 'composure'],
};

// Reference benchmark data (used when < 10 evaluations for age group)
const REFERENCE_BENCHMARK = {
  Technical: 6.5,
  Tactical: 6.0,
  Physical: 6.8,
  'Psycho-Social': 6.2,
};

const PlayerDashboard: React.FC<PlayerDashboardProps> = ({ onBackToLanding, onExit, onBackToCoachDashboard, playerId, evaluationData, viewedByCoach = false }) => {
  const [evaluation, setEvaluation] = useState<Evaluation | null>(evaluationData || null);
  const [loading, setLoading] = useState(!evaluationData);
  const [error, setError] = useState<string | null>(null);
  const [ageGroupAverages, setAgeGroupAverages] = useState<AgeGroupAverages | null>(null);
  const [useBenchmark, setUseBenchmark] = useState(true);

  useEffect(() => {
    // Scroll to top on load
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const loadEvaluation = async () => {
      if (evaluationData) {
        // Ensure evaluationData has the proper structure
        console.log('Using provided evaluationData:', evaluationData);
        setEvaluation(evaluationData);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // If playerId is provided (coach viewing), use that endpoint
        const endpoint = playerId 
          ? buildApiUrl(`/evaluations/player/${playerId}`)
          : buildApiUrl('/evaluations/player/my-evaluation');
        
        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Both endpoints now return {evaluation: {...}} format
          if (data.evaluation) {
            // Ensure scores are properly parsed (handle both string and object formats)
            const evalData = data.evaluation;
            if (evalData.scores && typeof evalData.scores === 'string') {
              try {
                evalData.scores = JSON.parse(evalData.scores);
              } catch (e) {
                console.error('Error parsing scores:', e);
              }
            }
            // Log for debugging
            console.log('Evaluation loaded:', {
              id: evalData.id,
              scoresCount: evalData.scores ? Object.keys(evalData.scores).length : 0,
              scoreKeys: evalData.scores ? Object.keys(evalData.scores) : [],
              player: evalData.player
            });
            setEvaluation(evalData);
          } else {
            setError('You don\'t have any evaluations yet. Please contact your coach to get your first evaluation.');
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          if (response.status === 404) {
            // Show user-friendly message for no evaluations
            const errorMessage = errorData.detail || 'You don\'t have any evaluations yet. Please contact your coach to get your first evaluation.';
            setError(errorMessage);
          } else if (response.status === 403) {
            // Handle permission errors with a helpful message
            const errorMessage = errorData.detail || 'Access denied. Please contact your coach if you believe this is an error.';
            setError(errorMessage);
          } else {
            setError(getErrorMessage(errorData, 'Failed to load evaluation. Please try again.'));
          }
        }
      } catch (err) {
        console.error('Error loading evaluation:', err);
        setError('Failed to load evaluation. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadEvaluation();
  }, [evaluationData, playerId]);

  useEffect(() => {
    const fetchAgeGroupAverages = async () => {
      if (!evaluation) return;

      try {
        // Ensure age is a number
        const age = typeof evaluation.player.age === 'number' 
          ? evaluation.player.age 
          : parseInt(String(evaluation.player.age), 10);
        
        if (isNaN(age)) {
          console.error('Invalid age value:', evaluation.player.age);
          return;
        }
        
        const response = await fetch(buildApiUrl(`/evaluations/age-group-averages/${age}`));
        if (response.ok) {
          const data: AgeGroupAverages = await response.json();
          setAgeGroupAverages(data);
          setUseBenchmark(data.use_benchmark);
        }
      } catch (err) {
        console.error('Error fetching age group averages:', err);
      }
    };

    fetchAgeGroupAverages();
  }, [evaluation]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getAverageScoresForAge = (category: string): number => {
    if (ageGroupAverages && !useBenchmark) {
      // Get category average from age group data
      // The averages object contains indicator-level averages, so we need to calculate category average
      const categoryIndicators = INDICATOR_GROUPS[category as keyof typeof INDICATOR_GROUPS];
      if (categoryIndicators) {
        const categorySum = categoryIndicators.reduce((sum, indicator) => {
          return sum + (ageGroupAverages.averages[indicator] || 0);
        }, 0);
        return categorySum / categoryIndicators.length;
      }
      return REFERENCE_BENCHMARK[category as keyof typeof REFERENCE_BENCHMARK] || 0;
    }
    return REFERENCE_BENCHMARK[category as keyof typeof REFERENCE_BENCHMARK] || 0;
  };

  // Dummy data for improvement over time (Q2 2025 to Q4 2025) with varied progress patterns
  const improvementData = useMemo(() => {
    if (!evaluation) return [];
    
    // Get current scores as baseline
    const currentTechnical = evaluation.category_scores.Technical || 0;
    const currentTactical = evaluation.category_scores.Tactical || 0;
    const currentPhysical = evaluation.category_scores.Physical || 0;
    const currentPsychoSocial = evaluation.category_scores['Psycho-Social'] || 0;
    
    // Create varied improvement trends for each category
    // Technical: Slow start, big jump in Q3, steady in Q4
    const q2Technical = currentTechnical * 0.85;
    const q3Technical = currentTechnical * 0.97;
    const q4Technical = currentTechnical;
    
    // Tactical: Steady improvement throughout
    const q2Tactical = currentTactical * 0.88;
    const q3Tactical = currentTactical * 0.94;
    const q4Tactical = currentTactical;
    
    // Physical: Big jump early, then plateau
    const q2Physical = currentPhysical * 0.83;
    const q3Physical = currentPhysical * 0.96;
    const q4Physical = currentPhysical * 0.99;
    
    // Psycho-Social: Gradual steady improvement
    const q2PsychoSocial = currentPsychoSocial * 0.90;
    const q3PsychoSocial = currentPsychoSocial * 0.95;
    const q4PsychoSocial = currentPsychoSocial;
    
    // Calculate Total Score as the weighted average of category scores at each point in time
    // Using relative weights: Technical 0.30, Tactical 0.25, Physical 0.25, Psycho-Social 0.20
    // (matching backend/ai/gpt.py DEFAULT_WEIGHTS)
    const weights = {
      'Technical': 0.30,
      'Tactical': 0.25,
      'Physical': 0.25,
      'Psycho-Social': 0.20,
    };
    
    const q2Total = (q2Technical * weights['Technical']) + 
                    (q2Tactical * weights['Tactical']) + 
                    (q2Physical * weights['Physical']) + 
                    (q2PsychoSocial * weights['Psycho-Social']);
    
    const q3Total = (q3Technical * weights['Technical']) + 
                    (q3Tactical * weights['Tactical']) + 
                    (q3Physical * weights['Physical']) + 
                    (q3PsychoSocial * weights['Psycho-Social']);
    
    const q4Total = (q4Technical * weights['Technical']) + 
                    (q4Tactical * weights['Tactical']) + 
                    (q4Physical * weights['Physical']) + 
                    (q4PsychoSocial * weights['Psycho-Social']);
    
    return [
      {
        quarter: 'Q2 2025',
        'Total Score': parseFloat(q2Total.toFixed(2)),
        'Technical': parseFloat(q2Technical.toFixed(2)),
        'Tactical': parseFloat(q2Tactical.toFixed(2)),
        'Physical': parseFloat(q2Physical.toFixed(2)),
        'Psycho-Social': parseFloat(q2PsychoSocial.toFixed(2)),
      },
      {
        quarter: 'Q3 2025',
        'Total Score': parseFloat(q3Total.toFixed(2)),
        'Technical': parseFloat(q3Technical.toFixed(2)),
        'Tactical': parseFloat(q3Tactical.toFixed(2)),
        'Physical': parseFloat(q3Physical.toFixed(2)),
        'Psycho-Social': parseFloat(q3PsychoSocial.toFixed(2)),
      },
      {
        quarter: 'Q4 2025',
        'Total Score': parseFloat(q4Total.toFixed(2)),
        'Technical': parseFloat(q4Technical.toFixed(2)),
        'Tactical': parseFloat(q4Tactical.toFixed(2)),
        'Physical': parseFloat(q4Physical.toFixed(2)),
        'Psycho-Social': parseFloat(q4PsychoSocial.toFixed(2)),
      },
    ];
  }, [evaluation]);

  // Calculate min and max scores for Y-axis scale with 0.25 increments
  const yAxisConfig = useMemo(() => {
    if (!improvementData || improvementData.length === 0) {
      return { min: 0, max: 10, ticks: [] };
    }
    
    let min = Infinity;
    let max = -Infinity;
    
    improvementData.forEach(dataPoint => {
      const scores = [
        dataPoint['Total Score'],
        dataPoint['Technical'],
        dataPoint['Tactical'],
        dataPoint['Physical'],
        dataPoint['Psycho-Social'],
      ];
      const pointMin = Math.min(...scores);
      const pointMax = Math.max(...scores);
      if (pointMin < min) {
        min = pointMin;
      }
      if (pointMax > max) {
        max = pointMax;
      }
    });
    
    // Round down min and round up max to nearest 0.25
    const roundedMin = Math.floor(min * 4) / 4;
    const roundedMax = Math.ceil(max * 4) / 4;
    
    // Generate ticks with 0.25 increments
    const ticks: number[] = [];
    for (let i = roundedMin; i <= roundedMax; i += 0.25) {
      ticks.push(parseFloat(i.toFixed(2)));
    }
    
    return {
      min: roundedMin,
      max: roundedMax,
      ticks: ticks,
    };
  }, [improvementData]);

  // Note: radarChartData is computed inline in the render section below

  const donutChartData = useMemo(() => {
    if (!evaluation) return { player: 0, average: 0, reference: 0 };
    
    const playerScore = parseFloat(evaluation.weighted_index.toFixed(2));
    const ageGroupAvg = ageGroupAverages && !useBenchmark
      ? Object.values(ageGroupAverages.averages).reduce((sum, val) => sum + val, 0) / Object.keys(ageGroupAverages.averages).length
      : Object.values(REFERENCE_BENCHMARK).reduce((sum, val) => sum + val, 0) / Object.keys(REFERENCE_BENCHMARK).length;
    
    // Reference benchmark is always the average of REFERENCE_BENCHMARK values
    const referenceScore = Object.values(REFERENCE_BENCHMARK).reduce((sum, val) => sum + val, 0) / Object.keys(REFERENCE_BENCHMARK).length;
    
    return {
      player: playerScore,
      average: parseFloat(ageGroupAvg.toFixed(2)),
      reference: parseFloat(referenceScore.toFixed(2)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluation, useBenchmark, ageGroupAverages]);

  const handleExit = () => {
    // Only clear authentication state if this is a true logout (no navigation handlers)
    // If there are navigation handlers, this is just navigation, not logout
    const hasNavigationHandler = onExit || onBackToLanding || onBackToCoachDashboard;
    
    if (!hasNavigationHandler) {
      // True logout - clear authentication state
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_id');
      localStorage.removeItem('user_role');
      localStorage.removeItem('username');
      localStorage.removeItem('is_admin');
    }
    
    // Call exit handler if provided, otherwise fall back to onBackToLanding
    if (onExit) {
      onExit();
    } else if (onBackToCoachDashboard) {
      onBackToCoachDashboard();
    } else if (onBackToLanding) {
      onBackToLanding();
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error || !evaluation) {
    // Determine which button/handler to use
    const handleBack = viewedByCoach && onBackToCoachDashboard 
      ? onBackToCoachDashboard 
      : handleExit;
    const buttonText = viewedByCoach && onBackToCoachDashboard 
      ? 'Back to Player List' 
      : 'Exit';
    
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>My Performance Evaluation</h1>
          <button onClick={handleBack} className="back-btn">{buttonText}</button>
        </div>
        <div className="error-message">{error || "You don't have any evaluations yet. Your coach will add evaluations for you."}</div>
      </div>
    );
  }

  const formatFirstName = (name: string) => {
    return name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };

  // Player photo placeholder - will be linked to database photo_url field in the future
  // For now, use a stock photo
  const playerPhoto = '/images/u20.jpg'; // Default stock photo - will be replaced with evaluation.player.photo_url when available

  return (
    <div className="dashboard">
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', flex: 1 }}>
          <img 
            src={logo} 
            alt="Futeval+ Logo" 
            style={{
              height: '180px',
              width: 'auto',
              opacity: 0.5,
              filter: 'brightness(0) saturate(100%) invert(1) brightness(1.2)'
            }}
          />
          {/* Player Photo Placeholder */}
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            border: '3px solid #0165fc',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#1f2230',
            flexShrink: 0
          }}>
            <img 
              src={playerPhoto} 
              alt={`${evaluation.player.first_name} ${evaluation.player.last_name}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center'
              }}
              onError={(e) => {
                // Fallback to default if image fails to load
                (e.target as HTMLImageElement).src = '/images/u20.jpg';
              }}
            />
          </div>
          <h1 style={{ 
            margin: 0, 
            fontSize: '2.5rem', 
            color: '#e0e0e0',
            fontWeight: 'normal',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{ fontWeight: 'bold' }}>{evaluation.player.last_name}</span>
            <span>,</span>
            <span> {formatFirstName(evaluation.player.first_name)}</span>
          </h1>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button 
            onClick={handlePrint} 
            className="back-btn"
            style={{
              padding: '0.5rem 1.2rem',
              fontSize: '0.9rem',
              minWidth: 'auto',
              width: 'auto',
              whiteSpace: 'nowrap',
              background: '#0165fc',
              borderColor: '#0165fc',
              color: '#ffffff',
              fontWeight: '600'
            }}
          >
            Print
          </button>
          {viewedByCoach && onBackToCoachDashboard ? (
            <button 
              onClick={onBackToCoachDashboard} 
              className="back-btn"
              style={{
                padding: '0.5rem 1.2rem',
                fontSize: '0.9rem',
                minWidth: 'auto',
                width: 'auto',
                whiteSpace: 'nowrap',
                background: '#567ad8',
                borderColor: '#567ad8',
                color: '#ffffff',
                fontWeight: '600'
              }}
            >
              Back to Coach Dashboard
            </button>
          ) : (
            <button 
              onClick={handleExit} 
              className="back-btn"
              style={{
                padding: '0.5rem 1.2rem',
                fontSize: '0.9rem',
                minWidth: 'auto',
                width: 'auto',
                whiteSpace: 'nowrap',
                color: '#ffffff',
                fontWeight: '600'
              }}
            >
              Exit
            </button>
          )}
        </div>
      </div>

      <div className="dashboard-content">
        {/* Player Info and Donut Chart on same level */}
        <div style={{ 
          display: 'flex', 
          gap: '2rem', 
          alignItems: 'flex-start',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginBottom: '2rem'
        }}>
          {/* Player Info */}
          <div className="player-info-card" style={{
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          }}>
            <div style={{
              marginBottom: '1.5rem',
              paddingBottom: '1.5rem',
              borderBottom: '1px solid rgba(1, 101, 252, 0.3)',
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#0165fc',
                textAlign: 'center',
              }}>
                {formatFirstName(evaluation.player.first_name)} {evaluation.player.last_name.toUpperCase()}
              </h3>
            </div>
            <div className="info-row" style={{ fontSize: '1.2rem', marginBottom: '1.2rem' }}>
              <span className="info-label" style={{ fontSize: '1.2rem' }}>Position:</span>
              <span className="info-value" style={{ fontSize: '1.2rem' }}>{evaluation.player.position}</span>
            </div>
            <div className="info-row" style={{ fontSize: '1.2rem', marginBottom: '1.2rem' }}>
              <span className="info-label" style={{ fontSize: '1.2rem' }}>Age:</span>
              <span className="info-value" style={{ fontSize: '1.2rem' }}>{evaluation.player.age}</span>
            </div>
            <div className="info-row" style={{ fontSize: '1.2rem', marginBottom: '1.2rem' }}>
              <span className="info-label" style={{ fontSize: '1.2rem' }}>Gender:</span>
              <span className="info-value" style={{ fontSize: '1.2rem' }}>{evaluation.player.gender || '-'}</span>
            </div>
            <div className="info-row" style={{ fontSize: '1.2rem', marginBottom: '1.2rem' }}>
              <span className="info-label" style={{ fontSize: '1.2rem' }}>Club:</span>
              <span className="info-value" style={{ fontSize: '1.2rem' }}>{evaluation.player.club || 'Shosholoza FC'}</span>
            </div>
            <div className="info-row" style={{ fontSize: '1.2rem', marginBottom: '1.2rem' }}>
              <span className="info-label" style={{ fontSize: '1.2rem' }}>Team:</span>
              <span className="info-value" style={{ fontSize: '1.2rem' }}>{evaluation.player.team || '-'}</span>
            </div>
            <div className="info-row" style={{ fontSize: '1.2rem', marginBottom: '1.2rem' }}>
              <span className="info-label" style={{ fontSize: '1.2rem' }}>Group:</span>
              <span className="info-value" style={{ fontSize: '1.2rem' }}>{evaluation.player.group || '-'}</span>
            </div>
          </div>

          {/* Overall Score Donut Chart */}
          <div className="chart-card" style={{ flex: '0 0 auto' }}>
          <h2>Overall Score</h2>
          <div style={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ position: 'relative', width: '250px', height: '250px', marginBottom: '1.5rem' }}>
              {/* Reference Benchmark Ring (outermost) */}
              <div style={{
                position: 'absolute',
                width: '250px',
                height: '250px',
                borderRadius: '50%',
                background: `conic-gradient(
                  rgba(1, 101, 252, 0.5) 0deg ${(donutChartData.reference / 10) * 360}deg,
                  transparent ${(donutChartData.reference / 10) * 360}deg 360deg
                )`,
                zIndex: 1,
              }} />
              
              {/* Age Group Average Ring (middle) */}
              <div style={{
                position: 'absolute',
                width: '220px',
                height: '220px',
                borderRadius: '50%',
                top: '15px',
                left: '15px',
                background: `conic-gradient(
                  rgba(1, 101, 252, 0.3) 0deg ${(donutChartData.average / 10) * 360}deg,
                  transparent ${(donutChartData.average / 10) * 360}deg 360deg
                )`,
                zIndex: 2,
              }} />
              
              {/* Player Score Ring (innermost) */}
              <div style={{
                position: 'absolute',
                width: '190px',
                height: '190px',
                borderRadius: '50%',
                top: '30px',
                left: '30px',
                background: `conic-gradient(
                  #0165fc 0deg ${(donutChartData.player / 10) * 360}deg,
                  transparent ${(donutChartData.player / 10) * 360}deg 360deg
                )`,
                zIndex: 3,
              }} />
              
              {/* Center circle with text */}
              <div style={{
                position: 'absolute',
                width: '145px',
                height: '145px',
                borderRadius: '50%',
                top: '52.5px',
                left: '52.5px',
                backgroundColor: '#1f2230',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                zIndex: 4,
              }}>
                <div style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: 'bold', 
                  color: '#0165fc',
                  textAlign: 'center',
                  lineHeight: '1.2',
                }}>
                  {donutChartData.player.toFixed(2)}
                </div>
                <div style={{ 
                  fontSize: '0.9rem', 
                  color: '#797b84',
                  textAlign: 'center',
                  lineHeight: '1.3',
                  marginTop: '0.5rem',
                }}>
                  Player Score
                </div>
              </div>
            </div>
            
            {/* Legend */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              width: '100%',
              maxWidth: '400px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#0165fc',
                }} />
                <span style={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                  Player Score: {donutChartData.player.toFixed(2)}
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(1, 101, 252, 0.3)',
                }} />
                <span style={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                  {useBenchmark ? 'Age Group Average' : `Age Group Average (${ageGroupAverages?.count || 0} players)`}: {donutChartData.average.toFixed(2)}
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(1, 101, 252, 0.5)',
                }} />
                <span style={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                  Reference Benchmark: {donutChartData.reference.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          </div>

          {/* Radar Charts - 2x2 Grid */}
          <div className="chart-card" style={{ flex: '0 0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: '1fr 1fr',
              gap: '1.5rem',
              rowGap: '2rem',
              width: '100%',
              maxWidth: '600px',
              height: '400px',
              alignContent: 'stretch',
            }}>
          {Object.entries(INDICATOR_GROUPS).map(([category, indicators]) => {
            // Filter out height and weight from Physical category for radar charts (they're not on 1-10 scale)
            const radarIndicators = category === 'Physical' 
              ? indicators.filter(ind => ind !== 'height' && ind !== 'weight')
              : indicators;
            
            const categoryData = radarIndicators.map(indicator => {
              // Get reference benchmark for this indicator (use category average as fallback)
              const referenceBenchmark = REFERENCE_BENCHMARK[category as keyof typeof REFERENCE_BENCHMARK] || 0;
              
              // Get age group average for this specific indicator
              const ageGroupAvg = ageGroupAverages && ageGroupAverages.averages[indicator] 
                ? ageGroupAverages.averages[indicator]
                : referenceBenchmark;
              
              // Get player score, ensuring it's a valid number
              const playerScore = evaluation.scores?.[indicator];
              
              // Convert to number, handling string values
              let playerValue = 0;
              if (playerScore !== null && playerScore !== undefined) {
                const numValue = typeof playerScore === 'string' ? parseFloat(playerScore) : Number(playerScore);
                if (!isNaN(numValue) && isFinite(numValue)) {
                  playerValue = numValue;
                }
              }
              
              // Debug logging for missing indicators
              if (playerValue === 0 && evaluation.scores && !(indicator in evaluation.scores)) {
                console.warn(`Missing indicator "${indicator}" in evaluation scores. Available:`, Object.keys(evaluation.scores));
              }
              
              return {
                indicator: indicator.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
                player: playerValue,
                reference: referenceBenchmark,
                ageGroupAvg: ageGroupAvg,
              };
            });

            return (
              <div key={category} className="radar-chart-card" style={{
                height: '100%',
                padding: '0.75rem',
              }}>
                <h3 style={{ 
                  fontSize: '0.9rem', 
                  marginBottom: '0.5rem',
                  textAlign: 'center',
                }}>{category}</h3>
                <ResponsiveContainer width="100%" height="85%">
                  <RadarChart data={categoryData}>
                    <PolarGrid />
                    {/* @ts-ignore - recharts type compatibility issue with React 19 */}
                    <PolarAngleAxis
                      dataKey="indicator"
                      tick={{ fontSize: 8 }}
                    />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 10]}
                      tick={{ fill: '#797b84', fontSize: 8 }}
                    />
                    {/* Render Reference Benchmark first (bottom layer) */}
                    <Radar
                      name="Reference Benchmark"
                      dataKey="reference"
                      stroke="#ff6b6b"
                      fill="#ff6b6b"
                      fillOpacity={0.3}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                    />
                    {/* Render Age Group Average second (middle layer) */}
                    <Radar
                      name={`Age Group Avg (${ageGroupAverages?.count || 0} players)`}
                      dataKey="ageGroupAvg"
                      stroke="#4ade80"
                      fill="#4ade80"
                      fillOpacity={0.4}
                      strokeWidth={2}
                    />
                    {/* Render Player Score last (top layer - most visible) */}
                    <Radar
                      name="Player"
                      dataKey="player"
                      stroke="#0165fc"
                      fill="#0165fc"
                      fillOpacity={0.6}
                      strokeWidth={3}
                    />
                    <Tooltip
                      formatter={(value: any) => {
                        if (typeof value === 'number') {
                          return value.toFixed(2);
                        }
                        return value;
                      }}
                      contentStyle={{
                        backgroundColor: 'rgba(1, 21, 44, 0.95)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: '2px solid #0165fc',
                        borderRadius: '12px',
                        color: '#e0e0e0',
                        padding: '0.75rem 1rem',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.8)'
                      }}
                      labelStyle={{
                        color: '#0165fc',
                        fontWeight: '600',
                        marginBottom: '0.5rem'
                      }}
                      itemStyle={{
                        color: '#e0e0e0'
                      }}
                      wrapperStyle={{
                        borderRadius: '12px'
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            );
          })}
            </div>
            
            {/* Legend for Radar Charts - Directly beneath the grid */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '2rem',
              marginTop: '1.5rem',
              padding: '1rem',
              backgroundColor: '#1f2230',
              borderRadius: '8px',
              border: '1px solid #383b48',
              flexWrap: 'wrap',
              width: '100%',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}>
                <div style={{
                  width: '20px',
                  height: '3px',
                  backgroundColor: '#0165fc',
                }} />
                <span style={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                  Player Score
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}>
                <div style={{
                  width: '20px',
                  height: '3px',
                  backgroundColor: '#4ade80',
                  border: 'none',
                }} />
                <span style={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                  Age Group Average
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}>
                <svg width="20" height="3" style={{ overflow: 'visible' }}>
                  <line
                    x1="0"
                    y1="1.5"
                    x2="20"
                    y2="1.5"
                    stroke="#ff6b6b"
                    strokeWidth="2"
                    strokeDasharray="3 3"
                  />
                </svg>
                <span style={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                  Reference Benchmark
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Improvement Over Time Line Chart */}
        <div className="chart-card">
          <h2>Performance Improvement Over Time</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={improvementData}>
              <XAxis 
                dataKey="quarter" 
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                domain={[yAxisConfig.min, yAxisConfig.max]} 
                style={{ fontSize: '12px' }}
                ticks={yAxisConfig.ticks}
                allowDecimals={true}
              />
              <Tooltip
                formatter={(value: any) => {
                  if (typeof value === 'number') {
                    return value.toFixed(2);
                  }
                  return value;
                }}
                contentStyle={{
                  backgroundColor: 'rgba(1, 21, 44, 0.95)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '2px solid #0165fc',
                  borderRadius: '12px',
                  color: '#e0e0e0',
                  padding: '0.75rem 1rem',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.8)'
                }}
                labelStyle={{
                  color: '#0165fc',
                  fontWeight: '600',
                  marginBottom: '0.5rem'
                }}
                itemStyle={{
                  color: '#e0e0e0'
                }}
                wrapperStyle={{
                  borderRadius: '12px'
                }}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '1rem' }}
                iconType="line"
              />
              <Line 
                type="monotone" 
                dataKey="Total Score" 
                stroke="#0165fc" 
                strokeWidth={3}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
              />
              <Line 
                type="monotone" 
                dataKey="Technical" 
                stroke="#ffb3d9" 
                strokeWidth={3}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
              />
              <Line 
                type="monotone" 
                dataKey="Tactical" 
                stroke="#b3ffb3" 
                strokeWidth={3}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
              />
              <Line 
                type="monotone" 
                dataKey="Physical" 
                stroke="#b366ff" 
                strokeWidth={3}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
              />
              <Line 
                type="monotone" 
                dataKey="Psycho-Social" 
                stroke="#fff4b3" 
                strokeWidth={3}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>


        {/* AI Feedback Section */}
        {evaluation.ai_feedback && (
          <div className="feedback-card card-subtle">
            <h3>Coach Feedback</h3>
            <div className={`ai-feedback-content ${evaluation.ai_feedback.includes('unavailable') || evaluation.ai_feedback.includes('Error:') ? 'ai-error' : ''}`}>
              {evaluation.created_at && (
                <div style={{
                  marginBottom: '1rem',
                  paddingBottom: '0.75rem',
                  borderBottom: '1px solid rgba(125, 211, 252, 0.2)',
                  fontSize: '0.9rem',
                  color: '#0165fc',
                  fontWeight: '500'
                }}>
                  Date Created: {new Date(evaluation.created_at).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              )}
              {evaluation.ai_feedback.split('\n').map((line, idx) => {
                const trimmedLine = line.trim();
                
                // Handle section headings (## OVERALL ASSESSMENT, ## KEY STRENGTHS, etc.)
                if (trimmedLine.startsWith('##')) {
                  const headingText = trimmedLine.replace(/^##\s*/, '').trim();
                  return (
                    <h4
                      key={idx}
                      style={{
                        marginTop: idx > 0 ? '2rem' : '0',
                        marginBottom: '1rem',
                        fontWeight: 'bold',
                        color: '#0165fc',
                        fontSize: '1.2rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {headingText}
                    </h4>
                  );
                }
                
                // Handle category sub-headings (### Technical, ### Tactical, etc.) - legacy format
                if (trimmedLine.startsWith('###')) {
                  const headingText = trimmedLine.replace(/^###\s*/, '').trim();
                  return (
                    <h4
                      key={idx}
                      style={{
                        marginTop: idx > 0 ? '1.5rem' : '0',
                        marginBottom: '0.75rem',
                        fontWeight: 'bold',
                        color: '#0165fc',
                        fontSize: '1.1rem'
                      }}
                    >
                      {headingText}
                    </h4>
                  );
                }
                
                // Handle bullet points (•, -, or * at start of line)
                const isBulletPoint = trimmedLine.match(/^[•\-*]\s+/);
                if (isBulletPoint) {
                  const bulletText = trimmedLine.replace(/^[•\-*]\s+/, '');
                  // Handle markdown-style bold (**text**)
                  const parts = bulletText.split(/(\*\*.*?\*\*)/g);
                  return (
                    <div key={idx} style={{ 
                      marginBottom: '0.5rem', 
                      marginLeft: '1.5rem',
                      paddingLeft: '0.75rem',
                      position: 'relative'
                    }}>
                      <span style={{ 
                        position: 'absolute', 
                        left: '-1.25rem', 
                        color: '#0165fc',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        lineHeight: '1.5'
                      }}>•</span>
                      <span style={{ color: '#e0e0e0' }}>
                        {parts.map((part, pIdx) => {
                          if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={pIdx} style={{ color: '#0165fc' }}>{part.slice(2, -2)}</strong>;
                          }
                          return <span key={pIdx}>{part}</span>;
                        })}
                      </span>
                    </div>
                  );
                }
                
                // Handle regular paragraphs (narrative text)
                if (trimmedLine) {
                  // Handle markdown-style bold (**text**)
                  const parts = line.split(/(\*\*.*?\*\*)/g);
                  return (
                    <p key={idx} style={{ marginBottom: '0.75rem', lineHeight: '1.6', color: '#e0e0e0' }}>
                      {parts.map((part, pIdx) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return <strong key={pIdx} style={{ color: '#0165fc' }}>{part.slice(2, -2)}</strong>;
                        }
                        return <span key={pIdx}>{part}</span>;
                      })}
                    </p>
                  );
                }
                
                // Empty lines - add spacing
                return <br key={idx} />;
              })}
            </div>
          </div>
        )}

        {!evaluation.ai_feedback && (
          <div className="feedback-card card-subtle">
            <h3>Coach Feedback</h3>
            <p style={{ color: '#797b84', fontStyle: 'italic' }}>
              Coach feedback is not available. Please contact your coach.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerDashboard;

